import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCities } from '@/_hooks/use-cities';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import { setFilterCityOnMonitor } from '@/_store/modules/filters/slice';
import { ENUM_DELIVERY_TYPE } from '@/_features/deliveries/constants';
import { EMonitorTab, MONITOR_SEARCH_PARAMS } from '../constants';

const isMonitorTab = (value: string | null): value is EMonitorTab =>
  Object.values(EMonitorTab).includes(value as EMonitorTab);

const isDeliveryType = (value: string | null): value is ENUM_DELIVERY_TYPE =>
  Object.values(ENUM_DELIVERY_TYPE).includes(value as ENUM_DELIVERY_TYPE);

export const normalizeMonitorCode = (code: string) => code.trim().toUpperCase();

export const useMonitorFilters = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cities, CITIES_OPTIONS, loadingCities } = useCities();
  const lastCityId = useAppSelector(state => state.filters.cityIdOnMonitor);

  const cityIdParam = searchParams.get(MONITOR_SEARCH_PARAMS.CITY_ID);
  const tabParam = searchParams.get(MONITOR_SEARCH_PARAMS.TAB);
  const code = searchParams.get(MONITOR_SEARCH_PARAMS.CODE) || undefined;
  const deliveryTypeParam = searchParams.get(
    MONITOR_SEARCH_PARAMS.DELIVERY_TYPE,
  );
  const deliveryType = isDeliveryType(deliveryTypeParam)
    ? deliveryTypeParam
    : undefined;
  const deliveryId =
    searchParams.get(MONITOR_SEARCH_PARAMS.DELIVERY_ID) || undefined;

  const isAllowedCity = (id?: string | null) =>
    !!id && !!cities?.some(city => city._id === id);

  const selectedCityId = lastCityId || cityIdParam;
  const cityId = isAllowedCity(selectedCityId)
    ? (selectedCityId as string)
    : undefined;
  const tab = isMonitorTab(tabParam) ? tabParam : EMonitorTab.PENDING;

  const updateSearchParam = (key: string, value?: string) => {
    setSearchParams(
      currentParams => {
        const nextParams = new URLSearchParams(currentParams);

        if (value) {
          nextParams.set(key, value);
        } else {
          nextParams.delete(key);
        }

        return nextParams;
      },
      { replace: true },
    );
  };

  const setCityId = (newCityId?: string) => {
    dispatch(setFilterCityOnMonitor(newCityId));
    updateSearchParam(MONITOR_SEARCH_PARAMS.CITY_ID, newCityId);
  };

  const setTab = (newTab: EMonitorTab) =>
    updateSearchParam(MONITOR_SEARCH_PARAMS.TAB, newTab);

  const setCode = (newCode: string) =>
    updateSearchParam(
      MONITOR_SEARCH_PARAMS.CODE,
      normalizeMonitorCode(newCode) || undefined,
    );

  const setDeliveryType = (newDeliveryType?: ENUM_DELIVERY_TYPE) =>
    updateSearchParam(MONITOR_SEARCH_PARAMS.DELIVERY_TYPE, newDeliveryType);

  const setDeliveryId = (newDeliveryId?: string) =>
    updateSearchParam(MONITOR_SEARCH_PARAMS.DELIVERY_ID, newDeliveryId);

  useEffect(() => {
    if (lastCityId) {
      updateSearchParam(MONITOR_SEARCH_PARAMS.CITY_ID, lastCityId);
    }
  }, [lastCityId]);

  useEffect(() => {
    if (loadingCities || !cities || cityId) {
      return;
    }

    if (cities.length === 1) {
      setCityId(cities[0]._id);
      return;
    }

    if (selectedCityId) {
      setCityId(undefined);
    }
  }, [cities, loadingCities, cityId]);

  useEffect(() => {
    if (cityId && cityId !== lastCityId) {
      dispatch(setFilterCityOnMonitor(cityId));
    }
  }, [cityId]);

  return {
    cityId,
    tab,
    code,
    deliveryType,
    deliveryId,
    setCityId,
    setTab,
    setCode,
    setDeliveryType,
    setDeliveryId,
    citiesOptions: CITIES_OPTIONS,
    loadingCities,
  };
};
